import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import {
    buildOnboardingRequest,
    listMerchantBankAccounts,
} from '../../../../api/onboarding/onboarding';
import { useOnboardingStore } from '../../../../store/admin/onboarding/onboardingStore';
import {
    getAllowedUrls,
    getMerchantKey,
    isAllowedMarket,
    isAllowedSegment,
    isBankAccountVerified,
    Merchant,
} from '../../../../types/onboarding/Merchant';
import { formatIban, MerchantBankAccount } from '../../../../types/onboarding/MerchantBankAccount';
import {
    OnboardingRequest,
    SalesSegment,
    salesSegments,
} from '../../../../types/onboarding/OnboardStore';
import {
    formatOnboardingCountry,
    getOnboardingCountries,
    getOnboardingCountryCurrency,
    isB2bAvailable,
} from '../../../../constants/onboardingCountries';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import { emailValidationConfig } from '../../../../types/ValidationTypes';
import { isPhoneValid } from '../../../../services/helpers/merchantSignup';
import FormRow from '../../../atoms/Form/FormRow';
import GridInput from '../../../atoms/Input/GridInput';
import SelectInput from '../../../atoms/Input/SelectInput';
import Radio from '../../../atoms/Input/Radio';
import HeaderTitle from '../../../atoms/Modal/HeaderTitle';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import FooterButtonContainer from '../../../atoms/Modal/FooterButtonContainer';
import Button from '../../../atoms/Button/Button';
import Spinner from '../../../atoms/Spinner/Spinner';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import ValidationError from '../../../atoms/Validation/ValidationError';
import Flex from '../../../atoms/Box/Flex';
import FlagIcon from '../../../atoms/FlagIcon/FlagIcon';

interface FormProps {
    organizationId: string;
    organizationName: string;
    /** Set for internal users so the API onboards the store for the selected organization. */
    groupCode?: string;
    onOnboard: (request: OnboardingRequest) => void;
    hide: () => void;
    theme: DefaultTheme;
}

type Inputs = {
    salesSegment: SalesSegment | '';
    countryCode: string;
    merchantKey: string;
    iban: string;
    /** The URL of the store, one of the allowed URLs of the selected merchant. */
    websiteUrl: string;
    /** Email and phone number printed on the invoices of the store, prefilled from the selected merchant. */
    email: string;
    phone: string;
    logoUrl: string;
    presentationName: string;
};

/** An https link whose path ends in a resource, not just a host or a folder such as https://cdn.example.com/. */
const isHttpsResourceUrl = (value: string) => {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.pathname.endsWith('/');
    } catch {
        return false;
    }
};

const logoUrlMessage = 'Enter the logo address as an https:// link to an SVG or PNG file.';

export const contactEmailMessage = 'Enter a valid email address for the invoice details.';
export const contactPhoneMessage = 'Enter a valid phone number for the invoice details.';

const isValidEmail = (email: string) => emailValidationConfig.emailRegExp.test(email.trim());

/** How long to wait after the last keystroke before requesting the logo preview. */
export const logoPreviewDelayMs = 400;

type LogoPreviewStatus = 'loading' | 'loaded' | 'failed';
type LogoPreviewState = { url: string; status: LogoPreviewStatus };
const noLogoPreview: LogoPreviewState = { url: '', status: 'loading' };

export const noVerifiedBankAccountMessage =
    'This merchant has no verified bank account. Verify one from the Merchants tab before onboarding a store.';

/** The merchant has verified accounts, but none in the currency a store in the chosen country settles in. */
export const noVerifiedBankAccountInCurrencyMessage = (currency: string) =>
    `This merchant has no verified ${currency} bank account, which a store in this market settles to. Verify one from the Merchants tab or choose another market.`;

/** The merchant was added without allowed URLs, so there is nothing a store can be onboarded on. */
export const noAllowedUrlsMessage =
    'This merchant has no allowed URLs. Allowed URLs are given when the merchant is added, and a store can only be onboarded on one of them.';

/** What the customer sees of the store on its invoices and in checkout: name, URL, email, phone and logo. */
export const invoiceDetailsHint =
    'Shown on the invoices of the store and in checkout. The URL is one of the allowed URLs of the merchant, and the email and phone number are prefilled from the merchant. Leave the friendly name blank to use the organization name. The logo is an https:// link to an SVG or PNG.';

/** Whether a store can be onboarded in the country for the segment and, once picked, the merchant's allowed markets. */
const isCountryAvailable = (countryCode: string, segment: SalesSegment | '', merchant?: Merchant) =>
    (segment !== 'B2B' || isB2bAvailable(countryCode)) &&
    (!merchant || isAllowedMarket(merchant, countryCode));

/** Whether a store can be onboarded in the segment given the allowed segments of the merchant, once one is picked. */
const isSegmentAvailable = (segment: SalesSegment | '', merchant?: Merchant) =>
    !merchant || isAllowedSegment(merchant, segment);

const noBankAccounts: MerchantBankAccount[] = [];
const noAllowedUrls: string[] = [];

/**
 * The option to use for a pick that must be one of the given options: the only option when there is exactly one,
 * the picked one when it is offered, otherwise none. Keeps the form free of writing picks back when options change.
 */
export const pickFrom = (picked: string, options: string[]): string => {
    if (options.length === 1) return options[0];

    return options.includes(picked) ? picked : '';
};

const OnboardStoreForm: React.FC<FormProps> = ({
    organizationId,
    organizationName,
    groupCode,
    onOnboard,
    hide,
    theme,
}: FormProps) => {
    const {
        merchants,
        isLoadingMerchants,
        bankAccounts,
        bankAccountsMerchantKey,
        isLoadingBankAccounts,
        bankAccountsError,
        isOnboardingStore,
        onboardStoreError,
        listBankAccountsStarted,
        listBankAccountsSucceeded,
        listBankAccountsFailed,
        clearBankAccounts,
    } = useOnboardingStore();

    const isFieldDisabled = isOnboardingStore || merchants.length === 0;

    const [logoPreview, setLogoPreview] = useState<LogoPreviewState>(noLogoPreview);

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<Inputs>({
        defaultValues: {
            salesSegment: '',
            countryCode: '',
            merchantKey: merchants.length === 1 ? getMerchantKey(merchants[0]) : '',
            iban: '',
            websiteUrl: '',
            email: '',
            phone: '',
            logoUrl: '',
            presentationName: '',
        },
    });

    const salesSegment = register('salesSegment', { required: true });
    const logoUrl = register('logoUrl', {
        validate: value => !value.trim() || isHttpsResourceUrl(value.trim()) || logoUrlMessage,
    });
    const presentationName = register('presentationName');

    const [
        watchedSegment,
        watchedCountryCode,
        watchedMerchantKey,
        watchedIban,
        watchedWebsiteUrl,
        watchedEmail,
        watchedPhone,
        watchedLogoUrl,
    ] = watch([
        'salesSegment',
        'countryCode',
        'merchantKey',
        'iban',
        'websiteUrl',
        'email',
        'phone',
        'logoUrl',
    ]);
    const logoUrlValue = (watchedLogoUrl ?? '').trim();

    // Wait until the user pauses typing before requesting the preview, so that every
    // half-typed address does not fire a request and flash a failure message.
    useEffect(() => {
        const url = isHttpsResourceUrl(logoUrlValue) ? logoUrlValue : '';
        const showPreview = () =>
            setLogoPreview(current => (current.url === url ? current : { url, status: 'loading' }));

        if (!url) {
            showPreview();
            return;
        }
        const timer = window.setTimeout(showPreview, logoPreviewDelayMs);
        return () => window.clearTimeout(timer);
    }, [logoUrlValue]);

    const setLogoPreviewStatus = (url: string, status: LogoPreviewStatus) =>
        setLogoPreview(current => (current.url === url ? { url, status } : current));

    const renderLogoPreview = () => {
        const { url, status } = logoPreview;
        if (!url) return null;

        if (status === 'failed')
            return (
                <LogoPreview data-testid="logo-preview-box" $loaded={false}>
                    <PreviewText>
                        We could not load the logo. Check that the link opens an image.
                    </PreviewText>
                </LogoPreview>
            );

        // The image stays mounted but hidden while loading, so that it can report load or error.
        return (
            <LogoPreview data-testid="logo-preview-box" $loaded={status === 'loaded'}>
                {status === 'loading' && <PreviewText>Loading the logo preview…</PreviewText>}
                <PreviewImage
                    key={url}
                    src={url}
                    alt="Logo preview"
                    data-testid="logo-preview"
                    $visible={status === 'loaded'}
                    onLoad={() => setLogoPreviewStatus(url, 'loaded')}
                    onError={() => setLogoPreviewStatus(url, 'failed')}
                />
            </LogoPreview>
        );
    };

    const findMerchant = (merchantKey: string) =>
        merchants.find(merchant => getMerchantKey(merchant) === merchantKey);

    const selectedMerchant = findMerchant(watchedMerchantKey);

    // The invoice email and phone start out as those of the selected merchant, for the user to keep or change. Picking
    // another merchant replaces them, since they belonged to the merchant that was picked before. The inputs are
    // remounted on the merchant key so that they show the new details as their default value, which also puts
    // their label where a filled or empty input wants it; a value written into a mounted input would not.
    const selectedMerchantKey = selectedMerchant ? getMerchantKey(selectedMerchant) : '';
    const merchantEmail = selectedMerchant?.contactInformation?.email ?? '';
    const merchantPhone = selectedMerchant?.contactInformation?.phone ?? '';

    useEffect(() => {
        setValue('email', merchantEmail);
        setValue('phone', merchantPhone);
    }, [selectedMerchantKey, merchantEmail, merchantPhone, setValue]);

    // The only merchant is picked for the user, since the select is disabled then. Also when the merchants arrive after
    // the form has mounted: the guided setup requests them as it opens the form, so the default value saw none.
    useEffect(() => {
        if (merchants.length !== 1) return;

        const onlyMerchantKey = getMerchantKey(merchants[0]);

        if (watchedMerchantKey !== onlyMerchantKey) setValue('merchantKey', onlyMerchantKey);
    }, [merchants, watchedMerchantKey, setValue]);

    // A merchant may be limited to some markets and segments, so a pick the selected merchant does not allow is cleared.
    useEffect(() => {
        if (watchedSegment && !isSegmentAvailable(watchedSegment, selectedMerchant))
            setValue('salesSegment', '');
    }, [watchedSegment, selectedMerchant, setValue]);

    useEffect(() => {
        if (
            watchedCountryCode &&
            !isCountryAvailable(watchedCountryCode, watchedSegment, selectedMerchant)
        )
            setValue('countryCode', '');
    }, [watchedSegment, watchedCountryCode, selectedMerchant, setValue]);

    // The only segment and market the merchant allows are picked for the user, like the only merchant. The clearing
    // above runs first, so a pick the merchant does not allow gives way to the one it does.
    const availableSegments = salesSegments.filter(segment =>
        isSegmentAvailable(segment.value, selectedMerchant)
    );
    const onlySegment = availableSegments.length === 1 ? availableSegments[0].value : '';
    const availableCountries = getOnboardingCountries().filter(country =>
        isCountryAvailable(country.code, watchedSegment, selectedMerchant)
    );
    const onlyCountryCode = availableCountries.length === 1 ? availableCountries[0].code : '';

    useEffect(() => {
        if (onlySegment && watchedSegment !== onlySegment) setValue('salesSegment', onlySegment);
    }, [onlySegment, watchedSegment, setValue]);

    useEffect(() => {
        if (onlyCountryCode && watchedCountryCode !== onlyCountryCode)
            setValue('countryCode', onlyCountryCode);
    }, [onlyCountryCode, watchedCountryCode, setValue]);

    // The settlement account must be one of the selected merchant's verified bank accounts, so they are
    // fetched when the merchant changes. A merchant whose verification is not complete has none to fetch.
    useEffect(() => {
        const merchant = merchants.find(
            candidate => getMerchantKey(candidate) === watchedMerchantKey
        );

        if (!merchant || !isBankAccountVerified(merchant)) {
            clearBankAccounts();
            return;
        }

        listMerchantBankAccounts(
            organizationId,
            merchant,
            listBankAccountsStarted,
            listBankAccountsSucceeded,
            listBankAccountsFailed
        );
    }, [
        organizationId,
        watchedMerchantKey,
        merchants,
        clearBankAccounts,
        listBankAccountsStarted,
        listBankAccountsSucceeded,
        listBankAccountsFailed,
    ]);

    const bankAccountsForMerchant =
        watchedMerchantKey && bankAccountsMerchantKey === watchedMerchantKey
            ? bankAccounts
            : noBankAccounts;

    // A store settles in the currency of its country, so only accounts in that currency can be picked once the
    // country is chosen.
    const currency = watchedCountryCode
        ? getOnboardingCountryCurrency(watchedCountryCode)
        : undefined;
    const bankAccountsForStore = useMemo(
        () =>
            currency
                ? bankAccountsForMerchant.filter(bankAccount => bankAccount.currency === currency)
                : bankAccountsForMerchant,
        [bankAccountsForMerchant, currency]
    );

    const allowedUrls = selectedMerchant ? getAllowedUrls(selectedMerchant) : noAllowedUrls;

    // The bank account and the store URL must be one of the options of the selected merchant. Rather than writing
    // the pick back into the form whenever the options change, the pick to use is derived from the raw pick and
    // the options: the only option counts as picked, and a pick that is no longer offered (another merchant or
    // market was chosen) does not count. The form keeps the raw pick, the request gets the derived one.
    const selectedIban = pickFrom(
        watchedIban,
        bankAccountsForStore.map(bankAccount => bankAccount.iban)
    );
    const selectedWebsiteUrl = pickFrom(watchedWebsiteUrl, allowedUrls);

    const countryOptions = availableCountries.map(country => ({
        value: country.code,
        label: (
            <Flex gap="1rem" alignItems="center">
                <FlagIcon countryCode={country.code} />
                {formatOnboardingCountry(country)}
            </Flex>
        ),
    }));

    const merchantOptions = merchants.map(merchant => ({
        value: getMerchantKey(merchant),
        label: `${merchant.name} (${merchant.organizationNumber})`,
    }));

    const bankAccountOptions = bankAccountsForStore.map(bankAccount => ({
        value: bankAccount.iban,
        label: `${formatIban(bankAccount.iban)} (${bankAccount.currency})`,
    }));

    const storeUrlOptions = allowedUrls.map(url => ({ value: url, label: url }));
    const hasNoAllowedUrls = !!selectedMerchant && allowedUrls.length === 0;
    // Shown once it is known that there is nothing to pick: the merchant has not completed the verification,
    // or its verified accounts were fetched and none fits (none at all, or none in the currency of the country).
    const hasNoVerifiedBankAccount =
        !!selectedMerchant &&
        !bankAccountsError &&
        (!isBankAccountVerified(selectedMerchant) ||
            (bankAccountsMerchantKey === watchedMerchantKey &&
                !isLoadingBankAccounts &&
                bankAccountsForStore.length === 0));
    const noVerifiedBankAccountText =
        currency && bankAccountsForMerchant.length > 0
            ? noVerifiedBankAccountInCurrencyMessage(currency)
            : noVerifiedBankAccountMessage;

    const canSubmit =
        !!watchedSegment &&
        isSegmentAvailable(watchedSegment, selectedMerchant) &&
        !!watchedCountryCode &&
        isCountryAvailable(watchedCountryCode, watchedSegment, selectedMerchant) &&
        !!selectedMerchant &&
        !!selectedIban &&
        !!selectedWebsiteUrl &&
        !!watchedEmail.trim() &&
        !!watchedPhone.trim() &&
        !isOnboardingStore;

    const onSubmit = (data: Inputs) => {
        const merchant = findMerchant(data.merchantKey);

        if (
            !merchant ||
            !data.salesSegment ||
            !data.countryCode ||
            !selectedIban ||
            !selectedWebsiteUrl
        )
            return;

        onOnboard(
            buildOnboardingRequest(merchant, {
                salesSegment: data.salesSegment,
                countryCode: data.countryCode,
                logoUrl: data.logoUrl.trim(),
                presentationName: data.presentationName.trim() || organizationName,
                iban: selectedIban,
                websiteUrl: selectedWebsiteUrl,
                email: data.email.trim(),
                phone: data.phone.trim(),
                groupCode,
            })
        );
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Onboard store</HeaderTitle>
            </ModalContainer>

            <ModalContainer position="content">
                <Form onSubmit={handleSubmit(onSubmit)} data-testid="form" id="onboard-store-form">
                    <FormRow first last>
                        <Controller
                            name="merchantKey"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <SelectInput
                                    id="merchant"
                                    dataTestId="merchant-select"
                                    options={merchantOptions}
                                    setRef={field.ref}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder={
                                        isLoadingMerchants ? 'Loading merchants...' : 'Merchant'
                                    }
                                    disabled={
                                        isFieldDisabled ||
                                        isLoadingMerchants ||
                                        merchants.length === 1
                                    }
                                />
                            )}
                        />
                    </FormRow>

                    <Group>
                        <SectionLabel id="segment-label">Segment</SectionLabel>
                        <Flex
                            gap={theme.layout.gap.xLarge}
                            role="radiogroup"
                            aria-labelledby="segment-label"
                        >
                            {availableSegments.map(segment => (
                                <Radio
                                    key={segment.value}
                                    id={`segment-${segment.value}`}
                                    name={salesSegment.name}
                                    value={segment.value}
                                    onChange={salesSegment.onChange}
                                    setRef={salesSegment.ref}
                                    disabled={isFieldDisabled}
                                    dataTestId={`segment-${segment.value}`}
                                >
                                    <RadioLabel htmlFor={`segment-${segment.value}`}>
                                        <span>{segment.label}</span>
                                        <RadioDescription>{segment.description}</RadioDescription>
                                    </RadioLabel>
                                </Radio>
                            ))}
                        </Flex>
                    </Group>

                    <Group>
                        <Rows>
                            <FormRow first>
                                <Controller
                                    name="countryCode"
                                    control={control}
                                    rules={{
                                        required: true,
                                        validate: (value, form) =>
                                            isCountryAvailable(
                                                value,
                                                form.salesSegment,
                                                findMerchant(form.merchantKey)
                                            ),
                                    }}
                                    render={({ field }) => (
                                        <SelectInput
                                            id="market"
                                            dataTestId="market-select"
                                            options={countryOptions}
                                            setRef={field.ref}
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder="Market"
                                            disabled={
                                                isFieldDisabled || countryOptions.length === 1
                                            }
                                        />
                                    )}
                                />
                            </FormRow>
                            <FormRow last>
                                <Controller
                                    name="iban"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectInput
                                            id="bankAccount"
                                            dataTestId="bank-account-select"
                                            options={bankAccountOptions}
                                            setRef={field.ref}
                                            value={selectedIban}
                                            onChange={field.onChange}
                                            placeholder={
                                                isLoadingBankAccounts
                                                    ? 'Loading bank accounts...'
                                                    : 'Bank account'
                                            }
                                            disabled={
                                                isFieldDisabled ||
                                                isLoadingBankAccounts ||
                                                bankAccountsForStore.length <= 1
                                            }
                                        />
                                    )}
                                />
                            </FormRow>
                        </Rows>
                        {hasNoVerifiedBankAccount ? (
                            <Hint data-testid="no-verified-bank-account">
                                {noVerifiedBankAccountText}
                            </Hint>
                        ) : (
                            <Hint>
                                Settlements for the store are paid to this verified bank account, in
                                the currency of the market.
                            </Hint>
                        )}
                    </Group>

                    <Group>
                        <SectionLabel>Invoice details</SectionLabel>
                        <Rows>
                            <FormRow first>
                                <Controller
                                    name="websiteUrl"
                                    control={control}
                                    render={({ field }) => (
                                        <SelectInput
                                            id="storeUrl"
                                            dataTestId="store-url-select"
                                            options={storeUrlOptions}
                                            setRef={field.ref}
                                            value={selectedWebsiteUrl}
                                            onChange={field.onChange}
                                            placeholder="Store URL"
                                            disabled={
                                                isFieldDisabled || storeUrlOptions.length <= 1
                                            }
                                        />
                                    )}
                                />
                            </FormRow>
                            <FormRow>
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{
                                        validate: value =>
                                            isValidEmail(value) || contactEmailMessage,
                                    }}
                                    render={({ field }) => (
                                        <GridInput
                                            key={selectedMerchantKey}
                                            type="email"
                                            id="contactEmail"
                                            dataTestId="contact-email"
                                            placeholder="Email address"
                                            setRef={field.ref}
                                            name={field.name}
                                            defaultValue={merchantEmail || undefined}
                                            onChange={field.onChange}
                                            disabled={isFieldDisabled}
                                            validationError={!!errors.email}
                                        />
                                    )}
                                />
                            </FormRow>
                            <FormRow>
                                <Controller
                                    name="phone"
                                    control={control}
                                    rules={{
                                        validate: value =>
                                            isPhoneValid(value) || contactPhoneMessage,
                                    }}
                                    render={({ field }) => (
                                        <GridInput
                                            key={selectedMerchantKey}
                                            type="tel"
                                            id="contactPhone"
                                            dataTestId="contact-phone"
                                            placeholder="Phone number"
                                            setRef={field.ref}
                                            name={field.name}
                                            defaultValue={merchantPhone || undefined}
                                            onChange={field.onChange}
                                            disabled={isFieldDisabled}
                                            validationError={!!errors.phone}
                                        />
                                    )}
                                />
                            </FormRow>
                            <FormRow>
                                <GridInput
                                    type="text"
                                    id="presentationName"
                                    dataTestId="presentation-name"
                                    placeholder="Friendly name (optional)"
                                    setRef={presentationName.ref}
                                    name={presentationName.name}
                                    onChange={presentationName.onChange}
                                    maxLength={100}
                                    disabled={isFieldDisabled}
                                />
                            </FormRow>
                            <FormRow last>
                                <GridInput
                                    type="url"
                                    id="logoUrl"
                                    dataTestId="logo-url"
                                    placeholder="Logo URL (optional)"
                                    setRef={logoUrl.ref}
                                    name={logoUrl.name}
                                    onChange={logoUrl.onChange}
                                    disabled={isFieldDisabled}
                                    validationError={!!errors.logoUrl}
                                />
                            </FormRow>
                        </Rows>
                        <LogoRow>
                            {hasNoAllowedUrls ? (
                                <Hint data-testid="no-allowed-urls">{noAllowedUrlsMessage}</Hint>
                            ) : (
                                <Hint>{invoiceDetailsHint}</Hint>
                            )}
                            {renderLogoPreview()}
                        </LogoRow>
                    </Group>
                </Form>
            </ModalContainer>

            {bankAccountsError && (
                <ModalContainer position="error">
                    <ErrorMessage
                        error={bankAccountsError}
                        errorHeader={ErrorType.LoadBankAccounts}
                    />
                </ModalContainer>
            )}
            {onboardStoreError && (
                <ModalContainer position="error">
                    <ErrorMessage error={onboardStoreError} errorHeader={ErrorType.OnboardStore} />
                </ModalContainer>
            )}
            {errors.email && (
                <ModalContainer position="error">
                    <ValidationError>{errors.email.message}</ValidationError>
                </ModalContainer>
            )}
            {errors.phone && (
                <ModalContainer position="error">
                    <ValidationError>{errors.phone.message}</ValidationError>
                </ModalContainer>
            )}
            {errors.logoUrl && (
                <ModalContainer position="error">
                    <ValidationError>{errors.logoUrl.message}</ValidationError>
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    <Button type="button" onClick={hide} large disabled={isOnboardingStore}>
                        Cancel
                    </Button>
                    <Button
                        form="onboard-store-form"
                        type="submit"
                        primary
                        large
                        disabled={!canSubmit}
                        data-testid="onboard-store-submit"
                    >
                        {isOnboardingStore ? (
                            <Spinner color={theme.colors.light} size={8} loading />
                        ) : (
                            <span>Onboard store</span>
                        )}
                    </Button>
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

/** The groups of the form, stacked with room between them. */
const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.layout.gap.medium};
`;

/** One group: its label, rows and hint, stacked close together. */
const Group = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.xsmall,
}))``;

/** Rows that share their borders, stacked with no room between them. */
const Rows = styled(Flex).attrs({ column: true })``;

const SectionLabel = styled.span`
    font-family: ${props => props.theme.text.font.medium};
    font-size: ${props => props.theme.text.size.medium};
    color: ${props => props.theme.colors.text.primary};
`;

const RadioLabel = styled.label`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    cursor: pointer;
    font-size: ${props => props.theme.text.size.medium};
`;

const RadioDescription = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;

const Hint = styled.p`
    margin: 0;
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;

/** The logo hint to the left and the preview to the right. */
const LogoRow = styled(Flex).attrs(props => ({
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: props.theme.layout.gap.small,
}))``;

/** A loaded logo sits on a grey and white checkerboard, so both white and dark logos stay visible in either theme. */
const checkerboard = `
    background-color: #ffffff;
    background-image:
        linear-gradient(45deg, #cfcfcf 25%, transparent 25%, transparent 75%, #cfcfcf 75%),
        linear-gradient(45deg, #cfcfcf 25%, transparent 25%, transparent 75%, #cfcfcf 75%);
    background-size: 1.2rem 1.2rem;
    background-position: 0 0, 0.6rem 0.6rem;
`;

const LogoPreview = styled.div<{ $loaded: boolean }>`
    flex: 0 0 auto;
    padding: 0.6rem;
    border: 0.1rem solid ${props => props.theme.colors.input.border};
    border-radius: 0.4rem;
    ${props => (props.$loaded ? checkerboard : `background: ${props.theme.colors.light};`)}
`;

/* The height is fixed because an SVG with only a viewBox has no size of its own and would collapse to nothing. */
const PreviewImage = styled.img<{ $visible: boolean }>`
    display: ${props => (props.$visible ? 'block' : 'none')};
    height: 4.4rem;
    width: auto;
    max-width: 12rem;
    object-fit: contain;
`;

const PreviewText = styled.span`
    font-size: ${props => props.theme.text.size.xsmall};
    color: ${props => props.theme.colors.text.secondary};
`;

export default withTheme(OnboardStoreForm) as React.ComponentType<Omit<FormProps, 'theme'>>;
